import Box from "@mui/material/Box";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import Loader from "layout/Loader";
import ExpensesSearchHeader from "components/Expenses/ExpensesSearchHeader";
import {
  useGetExpensesService,
  useGetExpenseTypeService,
} from "subModule/src/services/expense/useExpense";
import {
  CommonProfileService,
  ExpenseProfileService,
} from "subModule/src/core/services";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import { useNavigate } from "react-router-dom";
import { Expenses } from "subModule/src/services/expense/interface";
import FormDialog from "common/Modal/Modal";
import { DeleteData } from "subModule/src/type";
import ExpensesTable from "components/Expenses/ExpensesTable";
// import EditIcon from '@mui/icons-material/Edit';
// import DeleteIcon from '@mui/icons-material/Delete';
interface SearchFormData {
  [key: string]: string | any;
}

export default function ExpensesListing() {
  const navigate = useNavigate();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [row] = useState<Expenses>({});
  const [filter, setFilter] = useState<string[]>(["title", "type"]);
  const [search, setSearch] = useState<any>({});
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    title: "",
    type: 0,
    start: null,
    end: null,
    typeTitle: "",
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetExpensesService({ pageNumber: pageNo, size: pageSize, ...search });
  const { data: expenseTypeData, refetch: expenseTypeRefetch } =
    useGetExpenseTypeService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  useEffect(() => {
    expenseTypeRefetch();
  }, []);
  const fetchData = useCallback(() => {
    refetch()
      .then((res: any) => {
        if (!res?.data?.pages[0]?.success)
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.pages[0]?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.start, searchFormData.end]);
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "expenseTypes")
        expenseTypeRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  const DownloadExpenses = () => {
    CommonProfileService.exportEntityWithParams(
      { ...search },
      "expensemanager/export/excel",
    )
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Expenses.xlsx`);
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      })
      .finally(() => {
        setDownloading(false);
      });
  };
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  // const onEdit = (row: any) => navigate("/expense/" + row.id);
  const showRowView = (row: any) => navigate("/expense/view/" + row.id);
  const clearSearch = () => {
    setFilter(["title", "type"]);
    setSearchFormData({ title: "", type: 0 });
    setSearch({});
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    const selectedFilters =
      typeof value === "string" ? value.split(",") : value;
    setFilter(selectedFilters);
    setSearchFormData((prevFormData) => {
      const updatedFormData = Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          selectedFilters.includes(key)
            ? prevFormData[key]
            : typeof prevFormData[key] === "string"
              ? ""
              : 0,
        ]),
      );
      if (selectedFilters.includes("type") && prevFormData.typeTitle)
        updatedFormData.typeTitle = prevFormData.typeTitle; // ✅ retain typeTitle if type filter still selected
      if (selectedFilters.includes("dateInterval")) {
        updatedFormData.start = prevFormData.start;
        updatedFormData.end = prevFormData.end;
      }
      return updatedFormData;
    });
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val =
      field === "type" ? (value ? parseInt(value.value) || 0 : 0) : value;
    if (field === "dateInterval") {
      setSearchFormData((prevData) => ({
        ...prevData,
        start: value[0],
        end: value[1],
      }));
      if ((value[0] && value[1]) || (value[0] === null && value[1] === null)) {
        setLoading(true);
      }
    } else {
      setSearchFormData((prevData) => ({
        ...prevData,
        [field]: val,
        ...(field === "type" && value?.label ? { typeTitle: value.label } : {}),
      }));
    }
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number | boolean> = {};
    const { start, end, ...rest } = searchFormData;
    if (start && end) {
      filteredSearch.start = start;
      filteredSearch.end = end;
    } else {
      // ❗ If one or both missing → remove both
      setSearchFormData((prev) => ({
        ...prev,
        start: null,
        end: null,
      }));
    }
    Object.entries(rest).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
  };
  // const onDelete = (row: Expenses) => {
  //     setDeleteModal(true);
  //     setRow(row);
  // }
  // const actionslist = [
  //     // { label: "edit", onClick: onEdit, icon: <EditIcon /> }
  //     // { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  // ];
  // const adjustActions = (actions: any[]) => {
  //     return checkPrivileges('ROLE_EXPENSE_MANAGER_UPDATE')
  //       ? (checkPrivileges('ROLE_EXPENSE_MANAGER_ARCHIVE')
  //           ? actions
  //           : actions.filter(action => action.label !== 'delete'))
  //       : (checkPrivileges('ROLE_EXPENSE_MANAGER_ARCHIVE')
  //           ? actions.filter(action => action.label !== 'edit')
  //           : []);
  // }
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        heading={t("expenses")}
        onClick={() => navigate("/expense/add")}
        icon={
          <PriceChangeOutlinedIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"addExpense"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_EXPENSE_MANAGER_CREATE")}
      />
      {isFetching || loading ? (
        <Loader />
      ) : (
        <ExpensesTable
          columns={[
            { id: "title", label: "title", numeric: false, width: "18%" },
            { id: "typeTitle", label: "type", numeric: false, width: "12%" },
            {
              id: "supplierTitle",
              label: "supplier",
              numeric: false,
              width: "16%",
            },
            // { id: 'referenceNumber', label: 'referenceNo', numeric: false },
            { id: "amount", label: "amount", numeric: false, width: "12%" },
            {
              id: "stationTitle",
              label: "station",
              numeric: false,
              width: "16%",
            },
            { id: "date", label: "createdAt", numeric: false, width: "16%" },
            // { id: 'url', label: '', numeric: false },
            // ...(adjustActions(actionslist).length ? [{ id: 'actions', label: 'actions', numeric: false }] : []),
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          tableData={tableData?.pages[0]?.data || {}}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          showRowView={showRowView}
          // rowClick={checkPrivileges('ROLE_EXPENSE_MANAGER_UPDATE')}
          rowClick={true}
          {...{ pageNo, pageSize }}
          headerComponent={
            <ExpensesSearchHeader
              searchFormData={searchFormData}
              handleChangeFilter={handleChangeFilter}
              handleChangeSearch={handleChangeSearch}
              handleSubmitSearch={handleSubmitSearch}
              clearSearch={clearSearch}
              expenseTypeData={expenseTypeData?.pages[0]?.data?.content || []}
              expenseTypeResponse={expenseTypeData || {}}
              handleDropdownActions={handleDropdownActions}
              endActions={
                checkPrivileges("ROLE_EXPENSE_MANAGER_EXPORT") ? (
                  <Button
                    id="import-button"
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      setDownloading(true);
                      DownloadExpenses();
                    }}
                    className={`bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px] `}
                    sx={{
                      outline: "2px solid",
                      color: themeColors?.primary,
                      borderColor: themeColors?.primary,
                      height: 38,
                      minHeight: 38,
                    }}
                    disabled={downloading}
                  >
                    {downloading ? (
                      <CircularProgress
                        sx={{ color: themeColors?.primary, marginRight: "8px" }}
                        size={16}
                      />
                    ) : null}
                    {t("export")}
                  </Button>
                ) : null
              }
              {...{
                filter,
                search,
                dropdownPage,
                searchLoading,
                dropdownSearchTerm,
                setDropdownSearchTerm,
                setDropdownPage,
              }}
            />
          }
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showActions={true}
          // actions={adjustActions(actionslist)}
        />
      )}
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={(event, reason) =>
            event && reason !== "backdropClick" ? setDeleteModal(false) : ""
          }
          title={t("delete") + " " + t("expense")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setDeleteModal(false);
            setLoading(true);
            ExpenseProfileService.deleteExpense(row?.id || 0)
              .then((response: DeleteData) => {
                if (response.success) {
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("vehicle") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
    </Box>
  );
}
